/**
 * Порядок подборов в списке раздела «Подбор»: новые сверху.
 *
 * Даты создания приходят из двух подсистем в разных форматах:
 *  - zakooper `created_at` / `updated_at` — «YYYY-MM-DD HH:MM:SS» (или ISO);
 *  - group_search `date` — unix-секунды, мы маппим их в ISO;
 *  - локально созданные заголовки/значения могут быть в «DD.MM.YYYY HH:MM».
 *
 * Поэтому сравнивать строки лексикографически нельзя (именно это и «сбивало»
 * сортировку при склейке purchases + group_search): парсим в миллисекунды.
 */

/** Дата в формате «DD.MM.YYYY» с опциональным временем «HH:MM[:SS]». */
const DOTTED_DATE_RE = /^(\d{1,2})\.(\d{1,2})\.(\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/;

/** Метка для подборов без распознанной даты — они уходят в конец списка. */
const UNKNOWN_TIME = Number.NEGATIVE_INFINITY;

export interface SelectionRunOrderFields {
  createdAt?: string | null;
  updatedAt?: string | null;
}

/**
 * Разобрать серверную дату подбора в миллисекунды. Возвращает `null`, если
 * значение пустое или формат не распознан.
 */
export function parseSelectionDate(value?: string | null): number | null {
  if (!value) return null;
  const raw = value.trim();
  if (!raw) return null;

  const dotted = DOTTED_DATE_RE.exec(raw);
  if (dotted) {
    const [, day, month, year, hours = "0", minutes = "0", seconds = "0"] = dotted;
    return new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hours),
      Number(minutes),
      Number(seconds),
    ).getTime();
  }

  // «YYYY-MM-DD HH:MM:SS» — пробел вместо «T» разбирают не все движки.
  const ms = Date.parse(raw.includes(" ") ? raw.replace(" ", "T") : raw);
  return Number.isNaN(ms) ? null : ms;
}

/** Время создания подбора в миллисекундах; нераспознанное — «самое старое». */
export function selectionRunCreatedAtMs(run: SelectionRunOrderFields): number {
  return parseSelectionDate(run.createdAt) ?? UNKNOWN_TIME;
}

/** Сравнение по убыванию, безопасное для ±Infinity (в отличие от `b - a`). */
function compareDesc(a: number, b: number): number {
  if (a === b) return 0;
  return a > b ? -1 : 1;
}

/**
 * Компаратор «новые сверху»: по дате создания, при равенстве — по дате
 * последнего изменения. Подборы без даты — в конце.
 */
export function compareSelectionRunsByDateDesc(a: SelectionRunOrderFields, b: SelectionRunOrderFields): number {
  const byCreated = compareDesc(selectionRunCreatedAtMs(a), selectionRunCreatedAtMs(b));
  if (byCreated !== 0) return byCreated;
  return compareDesc(parseSelectionDate(a.updatedAt) ?? UNKNOWN_TIME, parseSelectionDate(b.updatedAt) ?? UNKNOWN_TIME);
}

/** Новая копия списка подборов, отсортированная «новые сверху». */
export function sortSelectionRunsByDateDesc<T extends SelectionRunOrderFields>(runs: readonly T[]): T[] {
  return [...runs].sort(compareSelectionRunsByDateDesc);
}
